import { useQuery } from '@tanstack/react-query'
import { apiFetch, hasLiveApi, hasLiveWallet, walletFetch } from '@/api/client'
import { toConsoleStatement, type WalletStatement } from '@/api/map/statement'
import * as api from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'
import type { DateRange } from '@/data/types'

export type ConsoleStatement = {
  rows: api.StatementRow[]
  openingBalance: number | null
  closingBalance: number | null
  /** True when there is no ledger source yet (avoid false reconcile errors). */
  unavailable?: boolean
}

async function loadStatement(
  id: string,
  range?: DateRange,
  currency?: string,
): Promise<ConsoleStatement> {
  // Live Keycloak: prefer hierarchy give/take audit (closes on live TB balance).
  // Fall back to wallet-service when configured; never invent fixture rows.
  // Pass the node's currencyType — live.currency is often hardcoded INR.
  if (hasLiveApi()) {
    try {
      const live = await apiFetch<WalletStatement>(`/v1/agents/${id}/statement`)
      return filterRange(toConsoleStatement(live, currency), range)
    } catch (err) {
      // Hierarchy statement missing / old binary — try wallet, else mark unavailable.
      if (hasLiveWallet()) {
        const live = await walletFetch<WalletStatement>(`/wallets/${id}/statement?limit=200`)
        return filterRange(toConsoleStatement(live, currency), range)
      }
      console.warn('[statement] hierarchy statement unavailable', err)
      return { rows: [], openingBalance: null, closingBalance: null, unavailable: true }
    }
  }
  return api.getStatement(id, range)
}

function filterRange(
  mapped: { rows: api.StatementRow[]; openingBalance: number; closingBalance: number },
  range?: DateRange,
): ConsoleStatement {
  if (!range) return mapped
  const from = range.from.slice(0, 10)
  const to = range.to.slice(0, 10)
  const rows = mapped.rows.filter((r) => {
    const day = r.at.slice(0, 10)
    return day >= from && day <= to
  })
  if (rows.length === 0) {
    // Empty window is not a ₹0 ledger — keep live closing so we don't false-alarm.
    return {
      rows: [],
      openingBalance: mapped.closingBalance,
      closingBalance: mapped.closingBalance,
    }
  }
  const closingBalance = rows[0]?.balanceAfter ?? mapped.closingBalance
  const oldest = rows[rows.length - 1]
  const openingBalance =
    oldest?.balanceBefore != null
      ? oldest.balanceBefore
      : oldest
        ? oldest.balanceAfter - oldest.amount
        : closingBalance
  return { rows, openingBalance, closingBalance }
}

export function useStatement(id: string | undefined, range?: DateRange, currency?: string) {
  return useQuery({
    queryKey: keys.statement(id ?? '', range, currency),
    queryFn: () => loadStatement(id!, range, currency),
    enabled: Boolean(id),
  })
}
